// apps/web/src/pages/PublicProfilePage.tsx
import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { PublicProfile } from '@hc-alumni/shared';
import { formatGraduateYear } from '@hc-alumni/shared/validation';
import {
  User,
  Building,
  GraduationCap,
  Mail,
  Phone,
  MessageSquare,
  ArrowLeft,
  Share2,
  Image as ImageIcon,
  Pencil,
  Briefcase,
  HandHelping,
} from 'lucide-react';
import ImageLightbox, { ZoomableImage } from '../components/ImageLightbox';
import SocialShare, { nativeShare } from '../components/SocialShare';
import { useAuth } from '../hooks/useAuth';

/** 全站預設封面；校友未自訂 coverImageUrl 時使用 */
export const DEFAULT_COVER_URL = '/cover-default.jpg';

type ProfileView = PublicProfile & {
  cardImageUrl?: string | null;
  coverImageUrl?: string | null;
  phone?: string | null;
  email?: string | null;
  isFaculty?: boolean;
  industry?: string | null;
  lookingFor?: string | null;
};

export default function PublicProfilePage() {
  const { id } = useParams<{ id: string }>();
  const authUser = useAuth((s) => s.user);
  const [lightbox, setLightbox] = useState<{ src: string; alt?: string } | null>(null);

  const openLightbox = (src: string, alt?: string) => setLightbox({ src, alt });
  const closeLightbox = () => setLightbox(null);

  const { data: profile, isLoading, error } = useQuery({
    queryKey: ['profile', id],
    queryFn: () => api.directory.get(id!) as Promise<ProfileView>,
    enabled: !!id,
    retry: 1,
  });

  const isSelf = !!(authUser?.id && id && authUser.id === id);
  /** 本人可編自己；代編僅主管理者（副管理者不可代編） */
  const canEdit = isSelf || !!authUser?.isOwner;

  if (isLoading) return <ProfileSkeleton />;
  if (error || !profile) return <NotFound />;

  const avatarSrc = profile.avatarUrl || profile.cardImageUrl || null;
  const coverSrc = profile.coverImageUrl || DEFAULT_COVER_URL;
  const initial = (profile.name || '?').charAt(0);

  const pageUrl =
    typeof window !== 'undefined'
      ? window.location.href
      : `https://fita.tworg.app/directory/${profile.id}`;
  const shareTitle = `和春校友會｜${profile.name}`;
  const shareText = `認識一下校友 ${profile.name}${
    profile.currentCompany ? ` - ${profile.currentCompany}` : ''
  }${profile.department ? `（${profile.department}）` : ''}`;

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: profile.name,
    jobTitle: profile.currentTitle || undefined,
    worksFor: profile.currentCompany
      ? { '@type': 'Organization', name: profile.currentCompany }
      : undefined,
    alumniOf: { '@type': 'CollegeOrUniversity', name: '和春技術學院' },
    image: avatarSrc || undefined,
    url: pageUrl,
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <div className="container-custom py-4">
        <Link
          to="/directory"
          className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-alumni-600 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          返回校友名冊
        </Link>

        <article className="card max-w-3xl mx-auto animate-fade-in overflow-hidden">
          <div className="relative">
            {/* 封面：固定比例；頭像可重疊，姓名永遠在白底區，不被封面壓住 */}
            <div className="w-full aspect-[21/9] sm:aspect-video overflow-hidden bg-hero-band">
              <ZoomableImage
                src={coverSrc}
                alt={`${profile.name} 的封面`}
                onOpen={openLightbox}
                className="w-full h-full"
                imgClassName="w-full h-full object-cover"
              />
            </div>

            {/* 頭像單獨上浮；文字區另起一列，不會因標籤變高而鑽進封面 */}
            <div className="relative z-10 px-5 sm:px-6">
              <div className="-mt-14 sm:-mt-16 flex justify-center sm:justify-start">
                <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-2xl overflow-hidden border-4 border-white dark:border-gray-950 shadow-xl bg-gray-100 dark:bg-gray-800 flex-shrink-0 ring-1 ring-black/5">
                  {avatarSrc ? (
                    <ZoomableImage
                      src={avatarSrc}
                      alt={profile.name}
                      onOpen={openLightbox}
                      className="w-full h-full"
                      imgClassName="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-hero-band">
                      <span className="text-4xl sm:text-6xl font-bold text-white">{initial}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="pt-3 sm:pt-4 pb-6 flex flex-col sm:flex-row sm:items-start gap-4">
                <div className="text-center sm:text-left flex-1 min-w-0">
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white tracking-tight">
                    {profile.name}
                  </h1>
                  <div className="mt-2 flex flex-wrap items-center justify-center sm:justify-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                    {profile.isFaculty && (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 dark:bg-indigo-900/30 px-3 py-1 text-indigo-800 dark:text-indigo-100 border border-indigo-100 dark:border-indigo-800 font-medium">
                        師長／教職員
                      </span>
                    )}
                    {(profile.department || profile.graduateYear != null) && (
                      <span className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-alumni-50 dark:bg-alumni-900/30 px-3 py-1 text-alumni-800 dark:text-alumni-100 border border-alumni-100 dark:border-alumni-800">
                        <GraduationCap className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">
                          {profile.department || '和春技術學院'}
                          {profile.graduateYear != null
                            ? ` · ${formatGraduateYear(profile.graduateYear)} 畢`
                            : profile.isFaculty
                              ? ''
                              : ''}
                        </span>
                      </span>
                    )}
                    {/* 公司 + 職稱：職稱≥3字或合計偏長時分行，避免「負責人」被拆行 */}
                    {(profile.currentCompany || profile.currentTitle) && (
                      <span className="inline-flex max-w-full items-start gap-1.5 rounded-2xl bg-gray-100 dark:bg-gray-800 px-3 py-1.5 border border-gray-200 dark:border-gray-700 text-left">
                        <Building className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span className="min-w-0 leading-snug">
                          {(() => {
                            const c = (profile.currentCompany || '').trim();
                            const t = (profile.currentTitle || '').trim();
                            const cl = Array.from(c).length;
                            const tl = Array.from(t).length;
                            const stack = c && t && (tl >= 3 || cl + tl > 12);
                            if (!c && t) return <span className="break-words">{t}</span>;
                            if (c && !t) return <span className="break-words">{c}</span>;
                            if (stack) {
                              return (
                                <>
                                  <span className="block break-words">{c}</span>
                                  <span className="block break-words text-gray-500 dark:text-gray-400 mt-0.5">
                                    {t}
                                  </span>
                                </>
                              );
                            }
                            return (
                              <span className="break-words">
                                {c}
                                {t ? (
                                  <span className="text-gray-500 dark:text-gray-400"> · {t}</span>
                                ) : null}
                              </span>
                            );
                          })()}
                        </span>
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex justify-center sm:justify-end gap-2 flex-shrink-0 flex-wrap">
                  {canEdit && (
                    <Link
                      to={isSelf ? '/profile' : `/profile/edit/${profile.id}`}
                      className="btn-primary px-4 py-2 flex items-center gap-2"
                    >
                      <Pencil className="w-4 h-4" />
                      {isSelf ? '編輯我的名片' : '代編名片'}
                    </Link>
                  )}
                  {profile.lineId && (
                    <a
                      href={`https://line.me/ti/p/~${profile.lineId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn bg-[#06C755] text-white px-4 py-2 flex items-center gap-2"
                    >
                      <MessageSquare className="w-4 h-4" />
                      加 LINE
                    </a>
                  )}
                  <button
                    type="button"
                    className="btn-secondary px-4 py-2 flex items-center gap-2"
                    onClick={() =>
                      nativeShare({ url: pageUrl, title: shareTitle, text: shareText })
                    }
                  >
                    <Share2 className="w-4 h-4" />
                    分享
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="p-6 sm:p-8 space-y-8">
            {/* 社群分享（參考說新聞：圖示列 + 手機原生分享） */}
            <section className="rounded-xl border border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40 p-4 sm:p-5">
              <SocialShare
                url={pageUrl}
                title={shareTitle}
                text={shareText}
                variant="full"
              />
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-alumni-600" />
                校友資訊
              </h2>
              <dl className="grid gap-3 sm:grid-cols-2">
                <InfoItem label="姓名" value={profile.name} />
                <InfoItem
                  label="身分"
                  value={profile.isFaculty ? '師長／教職員' : '校友（畢業生）'}
                />
                <InfoItem
                  label={profile.isFaculty ? '系所／單位' : '科系'}
                  value={profile.department}
                />
                <InfoItem
                  label="畢業年份"
                  value={
                    profile.graduateYear != null ? formatGraduateYear(profile.graduateYear) : null
                  }
                  emptyHint={profile.isFaculty ? '師長／教職員（可不填）' : '尚未填寫'}
                />
                <InfoItem
                  label={profile.isFaculty ? '任職單位／機構' : '現職公司'}
                  value={profile.currentCompany}
                  emptyHint="尚未填寫"
                />
                <InfoItem label="職稱" value={profile.currentTitle} emptyHint="尚未填寫" />
                <InfoItem
                  label="產業／產品"
                  value={profile.industry}
                  emptyHint="尚未填寫"
                />
              </dl>
            </section>

            {profile.lookingFor && (
              <section>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <HandHelping className="w-5 h-5 text-alumni-600" />
                  需要的東西／尋求資源
                </h2>
                <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed rounded-xl bg-amber-50/80 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/40 px-4 py-3">
                  {profile.lookingFor}
                </p>
              </section>
            )}

            {profile.industry && (
              <section className="sm:hidden">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-alumni-600" />
                  產業／產品
                </h2>
                <p className="text-gray-700 dark:text-gray-300">{profile.industry}</p>
              </section>
            )}

            {profile.bio && (
              <section>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <User className="w-5 h-5 text-alumni-600" />
                  關於我
                </h2>
                <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                  {profile.bio}
                </p>
              </section>
            )}

            <section>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-alumni-600" />
                名片
              </h2>
              {profile.cardImageUrl ? (
                <ZoomableImage
                  src={profile.cardImageUrl}
                  alt={`${profile.name} 的名片`}
                  onOpen={openLightbox}
                  className="block rounded-xl overflow-hidden border border-gray-100 dark:border-gray-700 max-w-md w-full"
                  imgClassName="w-full h-auto object-contain bg-gray-50 dark:bg-gray-900"
                />
              ) : (
                <div className="rounded-xl border border-dashed border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 p-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  此校友尚未上傳名片照片
                </div>
              )}
            </section>

            {(profile.email || profile.phone || profile.lineId) && (
              <section className="border-t border-gray-100 dark:border-gray-700 pt-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-alumni-600" />
                  聯絡資訊
                </h2>
                <div className="grid gap-3 sm:grid-cols-2">
                  {profile.email && (
                    <a
                      href={`mailto:${profile.email}`}
                      className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-lg bg-white dark:bg-gray-700 flex items-center justify-center shadow-sm">
                        <Mail className="w-5 h-5 text-alumni-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-gray-500 dark:text-gray-400">Email</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {profile.email}
                        </p>
                      </div>
                    </a>
                  )}
                  {profile.phone && (
                    <a
                      href={`tel:${profile.phone}`}
                      className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-lg bg-white dark:bg-gray-700 flex items-center justify-center shadow-sm">
                        <Phone className="w-5 h-5 text-alumni-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">手機</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {formatPhone(profile.phone)}
                        </p>
                      </div>
                    </a>
                  )}
                  {profile.lineId && (
                    <a
                      href={`https://line.me/ti/p/~${profile.lineId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-lg bg-[#06C755] flex items-center justify-center shadow-sm">
                        <MessageSquare className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">LINE</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {profile.lineId}
                        </p>
                      </div>
                    </a>
                  )}
                </div>
              </section>
            )}

            <footer className="border-t border-gray-100 dark:border-gray-700 pt-6 text-center text-sm text-gray-500 dark:text-gray-400">
              <p>此為校友公開分享檔案，資訊由本人維護</p>
              <p className="mt-1">和春技術學院校友會 &copy; {new Date().getFullYear()}</p>
            </footer>
          </div>
        </article>
      </div>

      <ImageLightbox
        open={!!lightbox}
        src={lightbox?.src || ''}
        alt={lightbox?.alt}
        onClose={closeLightbox}
      />
    </>
  );
}

function InfoItem({
  label,
  value,
  emptyHint = '—',
}: {
  label: string;
  value?: string | null;
  emptyHint?: string;
}) {
  return (
    <div className="rounded-lg bg-gray-50 dark:bg-gray-800/60 px-4 py-3">
      <dt className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">{label}</dt>
      <dd
        className={`text-sm font-medium ${
          value ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500 font-normal'
        }`}
      >
        {value || emptyHint}
      </dd>
    </div>
  );
}

function ProfileSkeleton() {
  return (
    <div className="container-custom py-8">
      <div className="max-w-3xl mx-auto card animate-pulse overflow-hidden">
        <div className="aspect-video bg-gray-200 dark:bg-gray-700" />
        <div className="relative z-10 px-6">
          <div className="-mt-14 sm:-mt-16 flex justify-center sm:justify-start">
            <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-2xl bg-gray-200 dark:bg-gray-700 border-4 border-white dark:border-gray-950" />
          </div>
          <div className="pt-4 pb-6 space-y-3">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mx-auto sm:mx-0" />
            <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mx-auto sm:mx-0" />
          </div>
        </div>
      </div>
    </div>
  );
}

function NotFound() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4">
      <div className="text-center">
        <User className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">找不到此校友</h2>
        <p className="text-gray-500 dark:text-gray-400 mb-6">
          此頁面不存在，或該校友尚未公開／尚未通過審核
        </p>
        <Link to="/directory" className="btn-primary inline-flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" />
          返回校友名冊
        </Link>
      </div>
    </div>
  );
}

function formatPhone(phone: string) {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.startsWith('886'))
    return `+886 ${cleaned.slice(3, 5)} ${cleaned.slice(5, 9)} ${cleaned.slice(9)}`;
  if (cleaned.startsWith('09'))
    return `${cleaned.slice(0, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7)}`;
  return phone;
}
